import { Select as SelectPrimitive } from "@base-ui/react/select";
import * as React from "react";

import { cn, cnState } from "@beweegbaas/ui/lib/utils";
import { IconCheck, IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons-react";

const Select = SelectPrimitive.Root;

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cnState("scroll-my-1 p-1", className)}
      {...props}
    />
  );
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cnState("flex flex-1 text-left", className)}
      {...props}
    />
  );
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: "sm" | "default";
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cnState(
        [
          `border-input data-[placeholder]:text-muted-foreground dark:bg-input/30
        dark:hover:bg-input/50 focus-visible:border-ring
        focus-visible:ring-ring/50 aria-invalid:ring-destructive/20
        dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive
        dark:aria-invalid:border-destructive/50 flex w-fit items-center
        justify-between gap-1.5 rounded-md border bg-transparent py-2
        pr-2 pl-2.5
        text-sm whitespace-nowrap shadow-xs
        transition-[color,box-shadow] outline-none
        focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-[3px]
        data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1
        *:data-[slot=select-value]:flex
        *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5
        [&_svg]:pointer-events-none [&_svg]:shrink-0
        [&_svg:not([class*='size-'])]:size-4`,

          // custom styles
          "squircle rounded-lg border-0 supports-squircle:rounded-full",
          "bg-clip-padding shadow-sm",
          "bg-white py-2 pr-2 pl-2.5 text-sm aria-invalid:ring-[3px]",
          "flex w-full cursor-default items-center gap-4 transition",
          "ring-input ring-1 hover:ring-black/20 dark:ring-white/10",
          "bg-clip-padding",
          // Dark theme
          "dark:bg-input/30 dark:hover:bg-input/50 dark:text-white",
          // Blue theme
          "blue:bg-white/10 blue:ring-white/40 blue:text-white",
          // Gradient style (background-image, so dark/blue need bg-none for their flat bg to show)
          "to-muted/50 bg-linear-to-b from-white dark:bg-none blue:bg-none",
        ],
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={<IconSelector className="text-muted-foreground pointer-events-none size-4" />}
      />
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  alignItemWithTrigger = true,
  collisionPadding,
  collisionBoundary,
  sticky,
  anchor,
  positionerClassName,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    | "align"
    | "alignOffset"
    | "side"
    | "sideOffset"
    | "alignItemWithTrigger"
    | "collisionPadding"
    | "collisionBoundary"
    | "sticky"
    | "anchor"
  > & {
    positionerClassName?: string;
  }) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        collisionPadding={collisionPadding}
        collisionBoundary={collisionBoundary}
        sticky={sticky}
        anchor={anchor}
        className={cn("isolate z-50", positionerClassName)}
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          className={cnState(
            `bg-popover text-popover-foreground
            data-open:not-data-[side=none]:animate-in
            data-closed:not-data-[side=none]:animate-out
            data-closed:fade-out-0 data-open:fade-in-0
            data-closed:zoom-out-95 data-open:zoom-in-95
            data-[side=bottom]:slide-in-from-top-2
            data-[side=left]:slide-in-from-right-2
            data-[side=right]:slide-in-from-left-2
            data-[side=top]:slide-in-from-bottom-2 ring-foreground/10 relative
            isolate z-50 max-h-(--available-height) w-(--anchor-width) min-w-36 origin-(--transform-origin) overflow-x-hidden
            overflow-y-auto rounded-md
            shadow-md ring-1 duration-100
            blue:bg-white/10 blue:text-white blue:ring-white/20 blue:backdrop-blur-xl`,
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cnState("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

function SelectItem({ className, children, ...props }: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cnState(
        `data-highlighted:bg-accent data-highlighted:text-accent-foreground
        not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground relative
        flex w-full cursor-default items-center gap-2 rounded-sm
        py-1.5 pr-8 pl-2
        text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none
        [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4
        *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2
        blue:data-highlighted:bg-white/15 blue:data-highlighted:text-white`,
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={
          <span
            className="pointer-events-none absolute right-2 flex size-4
              items-center justify-center"
          />
        }
      >
        <IconCheck className="pointer-events-none" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cnState("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cnState(
        `blue:bg-transparent bg-popover top-0 z-10 flex w-full cursor-default items-center
        justify-center py-1 [&_svg:not([class*='size-'])]:size-4`,
        className,
      )}
      {...props}
    >
      <IconChevronUp />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cnState(
        `blue:bg-transparent bg-popover bottom-0 z-10 flex w-full cursor-default items-center
        justify-center py-1 [&_svg:not([class*='size-'])]:size-4`,
        className,
      )}
      {...props}
    >
      <IconChevronDown />
    </SelectPrimitive.ScrollDownArrow>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectPrimitive,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
